import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import Footer from '../Footer';
import PageLoader from '../ui/PageLoader';
import HomeFeedPanel from './HomeFeedPanel';
import GarageSnapshot from './GarageSnapshot';
import type { AuthenticatedHomeData } from './homeTypes';
import ContributorRequestsPanel from '../contributors/ContributorRequestsPanel';
import { useOnboarding } from '../../hooks/useOnboarding';
import {
  OnboardingResumeCard,
  OnboardingWelcomeCard,
  SetupChecklistCard,
} from '../onboarding/OnboardingCards';
import {
  buildBuilderChecklist,
  checklistProgress,
  shouldShowBuilderChecklist,
  shouldShowOnboardingResume,
  shouldShowWelcome,
  type ChecklistStep,
} from '../../utils/onboarding';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../../utils/vehicleUrls';

/**
 * Authenticated home — chronological relevance feed as the primary daily dashboard.
 * Garage snapshot remains a compact secondary module. Public home is unchanged.
 */
export const AuthenticatedHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const onboarding = useOnboarding();
  const [garageData, setGarageData] = useState<AuthenticatedHomeData | null>(null);
  const [bootLoading, setBootLoading] = useState(true);

  const loadGarage = useCallback(async () => {
    setBootLoading(true);
    try {
      const home = await apiService.getHomeAuthenticated();
      setGarageData(home);
      void apiService.trackEvent('home_viewed', { metadata: { audience: 'authenticated', surface: 'feed' } });
    } catch {
      setGarageData({});
    } finally {
      setBootLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGarage();
  }, [loadGarage]);

  const garageVehicles = useMemo(() => garageData?.garage?.vehicles || [], [garageData?.garage?.vehicles]);

  const checklistSteps = useMemo(() => {
    if (!user?.username) return [] as ChecklistStep[];
    const snapshots = garageVehicles.map((vehicle) => {
      const completeness = (vehicle as { completeness?: { percent?: number } }).completeness;
      const slug = resolveVehicleNavSlug(vehicle);
      return {
        id: vehicle.id,
        slugPath: slug ? vehicleProfilePath(user.username, slug) : '/garage',
        engine: vehicle.engine,
        transmission: null,
        drivetrain: vehicle.drivetrain,
        chassis: vehicle.chassis,
        description: vehicle.description,
        imageCount: vehicle.image_url ? 1 : 0,
        modificationCount: vehicle.modification_count ?? 0,
        completenessPercent: completeness?.percent ?? 0,
        isPublic: vehicle.is_public,
      };
    });
    return buildBuilderChecklist(snapshots, onboarding.state, user.username);
  }, [garageVehicles, onboarding.state, user?.username]);

  const showWelcome = shouldShowWelcome(onboarding.state, garageVehicles.length);
  const showChecklist = shouldShowBuilderChecklist(onboarding.state, checklistSteps);
  const showResume = shouldShowOnboardingResume(onboarding.state, checklistSteps);

  const { loading: onboardingLoading, state: onboardingState, markStarted, markCompleted } =
    onboarding;

  useEffect(() => {
    if (!onboardingLoading && (showWelcome || showChecklist) && !onboardingState.started_at) {
      void markStarted();
    }
  }, [
    onboardingLoading,
    onboardingState.started_at,
    markStarted,
    showWelcome,
    showChecklist,
  ]);

  useEffect(() => {
    const { done, total } = checklistProgress(checklistSteps);
    if (total > 0 && done === total && !onboardingState.completed_at) {
      void markCompleted();
    }
  }, [checklistSteps, onboardingState.completed_at, markCompleted]);

  const handleChecklistAction = (step: ChecklistStep) => {
    if (step.actionId === 'add_vehicle') {
      navigate('/garage', { state: { openAddVehicle: true } });
      return;
    }
    if (step.href) {
      navigate(step.href);
    }
  };

  if (bootLoading) {
    return <PageLoader message="Loading your home…" />;
  }

  return (
    <div className="page relative overflow-x-hidden">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(249,115,22,0.06),transparent_50%)]"
        aria-hidden
      />
      <div className="film-grain opacity-[0.03]" aria-hidden />

      <div className="relative z-[1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 pb-4">
          {showWelcome ? (
            <OnboardingWelcomeCard
              onAddVehicle={() => navigate('/garage', { state: { openAddVehicle: true } })}
              onExplore={() => {
                void onboarding.dismissWelcome();
                navigate('/discover');
              }}
              onSkip={() => void onboarding.dismissWelcome()}
            />
          ) : null}

          {showChecklist && !showWelcome ? (
            <SetupChecklistCard
              steps={checklistSteps}
              onDismiss={() => void onboarding.dismissChecklist()}
              onStepAction={handleChecklistAction}
            />
          ) : null}

          {showResume && !showWelcome && !showChecklist ? (
            <OnboardingResumeCard onContinue={() => void onboarding.showSetupGuide()} />
          ) : null}

          <HomeFeedPanel />

          <div className="mt-10 border-t border-white/[0.05] pt-8">
            <GarageSnapshot garage={garageData?.garage} nextActions={garageData?.next_actions} />
          </div>

          <div className="py-4">
            <ContributorRequestsPanel />
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default AuthenticatedHomePage;
